// @catalog F.030
// WaterfallChart — cash-flow style. 6 pasos: Ventas brutas → Costo →
// Margen bruto (subtotal) → Descuentos → Comisiones → Margen neto (end).
import { Box, Tooltip } from "@mui/material";

export type WaterfallStepType = "start" | "neg" | "subtotal" | "end";

export interface WaterfallStep {
  label: string;
  // Para start/neg: value es signed (positivo = aporta, negativo = resta).
  // Para subtotal/end: value se ignora, se usa actualValue.
  value: number;
  type: WaterfallStepType;
  // Solo para subtotal/end: valor acumulado a representar en la barra.
  actualValue?: number;
  // Texto de ayuda (hover sobre el label): de dónde sale el dato y qué representa.
  tooltip?: string;
}

interface WaterfallChartProps {
  steps: ReadonlyArray<WaterfallStep>;
  formatValue: (v: number) => string;
}

export function WaterfallChart(props: WaterfallChartProps): JSX.Element {
  const { steps, formatValue } = props;
  const max = Math.max(
    ...steps.map((s) =>
      s.type === "subtotal" || s.type === "end"
        ? Math.abs(s.actualValue ?? 0)
        : Math.abs(s.value),
    ),
    1,
  );

  let cumulative = 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {steps.map((s, i) => {
        let displayVal: number;
        let barColor: string;
        let barWidth: number;
        let offset = 0;

        if (s.type === "start") {
          displayVal = s.value;
          barColor = "var(--ink-2)";
          barWidth = (s.value / max) * 100;
          cumulative = s.value;
        } else if (s.type === "neg") {
          displayVal = s.value;
          barColor = "var(--neg)";
          barWidth = (Math.abs(s.value) / max) * 100;
          cumulative += s.value;
          offset = (cumulative / max) * 100;
        } else if (s.type === "subtotal") {
          displayVal = s.actualValue ?? 0;
          barColor = "var(--ink-2)";
          barWidth = ((s.actualValue ?? 0) / max) * 100;
        } else {
          // end
          displayVal = s.actualValue ?? 0;
          barColor = "var(--accent)";
          barWidth = ((s.actualValue ?? 0) / max) * 100;
        }

        const isSubtotal = s.type === "subtotal" || s.type === "end";
        const labelNode = (
          <Box
            component="span"
            sx={{
              fontSize: "12px",
              color: isSubtotal ? "var(--ink-1)" : "var(--ink-2)",
              fontWeight: isSubtotal ? 600 : 400,
              ...(s.tooltip && {
                cursor: "help",
                textDecoration: "underline dotted",
                textUnderlineOffset: "2px",
              }),
            }}
          >
            {s.label}
          </Box>
        );

        return (
          <Box
            key={`${s.label}-${i}`}
            sx={{
              display: "grid",
              gridTemplateColumns: "120px 1fr 100px",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {s.tooltip ? (
              <Tooltip title={s.tooltip} arrow placement="top">
                {labelNode}
              </Tooltip>
            ) : (
              labelNode
            )}
            <Box
              sx={{
                position: "relative",
                height: "18px",
                backgroundColor: "var(--bg-2)",
                borderRadius: "3px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: `${offset}%`,
                  width: `${barWidth}%`,
                  top: 0,
                  bottom: 0,
                  backgroundColor: barColor,
                  borderRadius: "3px",
                  opacity: s.type === "subtotal" ? 0.5 : 1,
                }}
              />
            </Box>
            <Box
              component="span"
              sx={{
                textAlign: "right",
                fontSize: "12px",
                color: s.type === "neg" ? "var(--neg)" : "var(--ink-1)",
                fontWeight: isSubtotal ? 600 : 400,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {s.type === "neg" ? "−" : ""}
              {formatValue(Math.abs(displayVal))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
